import { BaseComponent } from '../utils/base-component';
import { EventHandler } from '../utils/dom/event-handler';
import { SelectorEngine } from '../utils/dom/selector-engine';
import { Backdrop } from '../utils/backdrop';
import { enableDismissTrigger } from '../utils/enable-dismiss-trigger';
import { FocusTrap } from '../utils/focus-trap';
import { isVisible, reflow } from '../utils/utilities';
import { ScrollbarHelper } from '../utils/scrollbar-helper';

import Plyr from 'plyr';

/**
 * Constants
 */

const NAME = 'modal';
const DATA_KEY = 'ssm.modal';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';
const ESCAPE_KEY = 'Escape';

const EVENT_HIDE = `hide${EVENT_KEY}`;
const EVENT_HIDE_PREVENTED = `hidePrevented${EVENT_KEY}`;
const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
const EVENT_SHOW = `show${EVENT_KEY}`;
const EVENT_SHOWN = `shown${EVENT_KEY}`;
const EVENT_RESIZE = `resize${EVENT_KEY}`;
const EVENT_CLICK_DISMISS = `click.dismiss${EVENT_KEY}`;
const EVENT_MOUSEDOWN_DISMISS = `mousedown.dismiss${EVENT_KEY}`;
const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY}`;
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const CLASS_NAME_OPEN = 'modal-open';
const CLASS_NAME_SHOW = 'show';
const CLASS_NAME_STATIC = 'modal-static';

const OPEN_SELECTOR = '.modal.show';
const SELECTOR_DIALOG = '.modal__wrapper';
const SELECTOR_MODAL_BODY = '.modal__body';
const SELECTOR_DATA_TOGGLE = '.js-modal-toggle, .js-create-modal';

const Config = {
    backdrop: true,
    focus: true,
    keyboard: true,
    destroyOnClose: false,
    data: null,
};

/**
 * Class definition
 */

export class Modal extends BaseComponent {
    constructor(element, config) {
        super(element, config);

        this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, this._element);
        this._backdrop = this._initializeBackDrop();
        this._focustrap = this._initializeFocusTrap();
        this._isShown = false;
        this._isTransitioning = false;
        this._scrollBar = new ScrollbarHelper();
        this._addEventListeners();
    }

    // Getters
    static get Default() {
        return Config;
    }

    static get NAME() {
        return NAME;
    }

    // Override BaseComponent's create method for dynamic modals
    create(data) {
        if (!data) return super.create();

        const info = JSON.parse(data.getAttribute('data-info') || '{}');
        if (!info) return super.create();

        const { title = '', description = '', file = '', tags = [], bg = 'gold', video = '', type = '', link = null } = info;

        // --- clean video URL only for embed providers ---
        let cleanVideo = video;
        const isVideoEmbedProvider = cleanVideo.includes('youtube.com') || cleanVideo.includes('youtu.be') || cleanVideo.includes('vimeo.com');

        if (isVideoEmbedProvider && cleanVideo.includes('/static')) {
            const httpIndex = cleanVideo.indexOf('http');

            if (httpIndex !== -1) {
                cleanVideo = cleanVideo.slice(httpIndex);
            }
        }

        // --- helper: video source ---
        const getVideoSource = (url, poster = '') => {
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
                return {
                    type: 'video',
                    sources: [{ src: url, provider: 'youtube' }],
                    poster,
                };
            } else if (url.includes('vimeo.com')) {
                return {
                    type: 'video',
                    sources: [{ src: url, provider: 'vimeo' }],
                    poster,
                };
            } else {
                return {
                    type: 'video',
                    sources: [{ src: url, provider: 'html5' }],
                    poster,
                };
            }
        };

        // --- create video element ---
        const isEmbed = cleanVideo.includes('youtube') || cleanVideo.includes('vimeo');
        const videoEl = document.createElement(isEmbed ? 'div' : 'video');

        if (!isEmbed) {
            videoEl.controls = true;
            videoEl.playsInline = true;
        } else {
            videoEl.classList.add('plyr__video-embed');

            const iframe = document.createElement('iframe');
            iframe.src = cleanVideo;
            iframe.allow = 'autoplay';
            iframe.allowFullscreen = true;
            iframe.allowTransparency = true;

            videoEl.appendChild(iframe);
        }

        // --- helpers for layout ---
        const buildTags = (tags) => (Array.isArray(tags) ? 
            tags.filter(tag => tag?.title).map((tag) => 
                `<span class="modal-tag">${tag.title}</span>`).join('') 
            : '');

        const buildFileButton = (file) => (file ? `<div class="wp-block-button"><a href="${file}" class="wp-block-button__link" download>${tahoTranslate.download}</a></div>` : '');

        const buildOpenButton = (link) => (link?.url ? `<div class="wp-block-button"><a href="${link.url}" class="wp-block-button__link" target="${link.target || '_self'}">${link.label || tahoTranslate.open}</a></div>` : '');

        const buildVideoBlock = (tagsHtml, title) => `
        <div class="modal__content-video-wrapper">
            <div class="modal__content-video wp-block-video"></div>
            <div>
                ${tagsHtml ? `<div class="modal__content-tags">${tagsHtml}</div>` : ''}
                ${title ? `<h2 class="text-xl md:text-md">${title}</h2>` : ''}

                <div class="modal__content-description text-base">
                    ${description ? `<p>${description}</p>` : ''}
                </div>
            </div>
        </div>
    `;

        const buildDefaultBlock = (tagsHtml, fileHtml) => `
        ${tagsHtml ? `<div class="modal__content-tags">${tagsHtml}</div>` : ''}

        <div class="modal__content-wrapper">
            <div class="modal__content-general">
                ${title ? `<h2>${title}</h2>` : ''}
                ${fileHtml}
            </div>

            <div class="modal__content-description text-base">
                ${description ? `<p>${description}</p>` : ''}
            </div>
        </div>
    `;

        // --- assemble modal ---
        const tagsHtml = buildTags(tags);
        const fileHtml = link ? buildOpenButton(link) : buildFileButton(file);
        const contentHtml = cleanVideo ? buildVideoBlock(tagsHtml, title) : buildDefaultBlock(tagsHtml, fileHtml);

        const modal = document.createElement('div');
        modal.classList.add('modal', 'hidden', 'group');
        modal.tabIndex = -1;
        modal.role = 'dialog';

        modal.innerHTML = `
        <div class="modal__wrapper group bg-${bg} ${type}">
            <div class="modal__header">
                <button type="button" data-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="modal__content">
                ${contentHtml}
            </div>
        </div>
    `;

        // --- insert video element in DOM ---
        if (cleanVideo) {
            const container = modal.querySelector('.wp-block-video');

            if (container) {
                container.appendChild(videoEl);

                const player = new Plyr(videoEl, {
                    autoplay: true,
                    muted: true,
                });

                player.source = getVideoSource(cleanVideo);

                player.on('ready', () => {
                    player.muted = true;
                    player.play();
                });
            }
        }

        return modal;
    }

    // Public
    toggle(relatedTarget) {
        return this._isShown ? this.hide() : this.show(relatedTarget);
    }

    show(relatedTarget) {
        if (this._isShown || this._isTransitioning) {
            return;
        }

        const showEvent = EventHandler.trigger(this._element, EVENT_SHOW, {
            relatedTarget,
        });

        if (showEvent.defaultPrevented) {
            return;
        }

        this._isShown = true;
        this._isTransitioning = true;

        this._scrollBar.hide();

        document.body.classList.add(CLASS_NAME_OPEN);

        this._adjustDialog();

        this._backdrop.show(() => this._showElement(relatedTarget));
    }

    hide() {
        if (!this._isShown || this._isTransitioning) {
            return;
        }

        const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE);

        if (hideEvent.defaultPrevented) {
            return;
        }

        this._isShown = false;
        this._isTransitioning = true;
        this._focustrap.deactivate();

        this._element.classList.remove(CLASS_NAME_SHOW);

        this._queueCallback(() => this._hideModal(), this._element, true);
    }

    dispose() {
        this._element.remove();

        EventHandler.off(window, EVENT_KEY);
        EventHandler.off(this._dialog, EVENT_KEY);

        this._backdrop.dispose();
        this._focustrap.deactivate();

        super.dispose();
    }

    handleUpdate() {
        this._adjustDialog();
    }

    // Private
    _initializeBackDrop() {
        return new Backdrop({
            isVisible: Boolean(this._config.backdrop),
            isAnimated: true,
        });
    }

    _initializeFocusTrap() {
        return new FocusTrap({
            trapElement: this._element,
        });
    }

    _showElement(relatedTarget) {
        // try to append dynamic modal
        if (!document.body.contains(this._element)) {
            document.body.append(this._element);
        }

        this._element.style.display = 'block';
        this._element.removeAttribute('aria-hidden');
        this._element.setAttribute('aria-modal', true);
        this._element.setAttribute('role', 'dialog');
        this._element.scrollTop = 0;

        const modalBody = SelectorEngine.findOne(SELECTOR_MODAL_BODY, this._dialog);
        if (modalBody) {
            modalBody.scrollTop = 0;
        }

        reflow(this._element);

        this._element.classList.add(CLASS_NAME_SHOW);

        const transitionComplete = () => {
            if (this._config.focus) {
                this._focustrap.activate();
            }

            this._isTransitioning = false;
            EventHandler.trigger(this._element, EVENT_SHOWN, {
                relatedTarget,
            });
        };

        this._queueCallback(transitionComplete, this._dialog, true);
    }

    _addEventListeners() {
        EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS, (event) => {
            if (event.key !== ESCAPE_KEY) {
                return;
            }

            if (this._config.keyboard) {
                this.hide();

                return;
            }

            this._triggerBackdropTransition();
        });

        EventHandler.on(window, EVENT_RESIZE, () => {
            if (this._isShown && !this._isTransitioning) {
                this._adjustDialog();
            }
        });

        EventHandler.on(this._element, EVENT_MOUSEDOWN_DISMISS, (event) => {
            EventHandler.one(this._element, EVENT_CLICK_DISMISS, (event2) => {
                if (this._element !== event.target || this._element !== event2.target) {
                    return;
                }

                if (this._config.backdrop === 'static') {
                    this._triggerBackdropTransition();

                    return;
                }

                if (this._config.backdrop) {
                    this.hide();
                }
            });
        });
    }

    _hideModal() {
        this._element.style.display = 'none';
        this._element.setAttribute('aria-hidden', true);
        this._element.removeAttribute('aria-modal');
        this._element.removeAttribute('role');
        this._isTransitioning = false;

        this._backdrop.hide(() => {
            document.body.classList.remove(CLASS_NAME_OPEN);
            this._resetAdjustments();
            this._scrollBar.reset();
            EventHandler.trigger(this._element, EVENT_HIDDEN);

            // Handle destroyOnClose
            if (this._config.destroyOnClose) {
                this.dispose();
            }
        });
    }

    _triggerBackdropTransition() {
        const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED);
        if (hideEvent.defaultPrevented) {
            return;
        }

        const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;
        const initialOverflowY = this._element.style.overflowY;

        if (initialOverflowY === 'hidden' || this._element.classList.contains(CLASS_NAME_STATIC)) {
            return;
        }

        if (!isModalOverflowing) {
            this._element.style.overflowY = 'hidden';
        }

        this._element.classList.add(CLASS_NAME_STATIC);
        this._queueCallback(() => {
            this._element.classList.remove(CLASS_NAME_STATIC);
            this._queueCallback(() => {
                this._element.style.overflowY = initialOverflowY;
            }, this._dialog);
        }, this._dialog);

        this._element.focus();
    }

    _adjustDialog() {
        const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;
        const scrollbarWidth = this._scrollBar.getWidth();
        const isBodyOverflowing = scrollbarWidth > 0;

        if (isBodyOverflowing && !isModalOverflowing) {
            this._element.style.paddingRight = `${scrollbarWidth}px`;
        }

        if (!isBodyOverflowing && isModalOverflowing) {
            this._element.style.paddingLeft = `${scrollbarWidth}px`;
        }
    }

    _resetAdjustments() {
        this._element.style.paddingLeft = '';
        this._element.style.paddingRight = '';
    }
}

/**
 * Data API implementation
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
    event.preventDefault();

    // Handle dynamic modal creation
    if (this.classList.contains('js-create-modal')) {
        const modalInstance = new Modal(null, {
            destroyOnClose: true,
            data: this,
        });
        modalInstance.show();

        return;
    }

    // Handle static modals
    const target = SelectorEngine.getElementFromSelector(this);
    const link = this;

    EventHandler.one(target, EVENT_SHOW, (showEvent) => {
        if (showEvent.defaultPrevented) {
            return;
        }

        EventHandler.one(target, EVENT_HIDDEN, () => {
            if (isVisible(link)) {
                link.focus();
            }
        });
    });

    const alreadyOpen = SelectorEngine.findOne(OPEN_SELECTOR);
    if (alreadyOpen) {
        Modal.getInstance(alreadyOpen).hide();
    }

    const data = Modal.getOrCreateInstance(target);
    data.toggle(link);
});

enableDismissTrigger(Modal);
